import { dict, type PanelLang } from './lang';

/**
 * Định dạng hiển thị — giờ Việt Nam.
 *
 * Gom một chỗ vì panel hiển thị cùng một kiểu dữ liệu ở hàng chục nơi, và
 * "3 giờ trước" ở trang này với "3h trước" ở trang kia là loại lệch nhỏ khiến
 * giao diện trông như ba người ghép lại.
 *
 * Số và mốc thời gian giữ nguyên quy ước Việt Nam ở CẢ HAI thứ tiếng: người xem
 * bản tiếng Anh vẫn ngồi cùng phòng với người xem bản tiếng Việt, và hai người
 * đọc `07/12` với `12/07` cho cùng một dòng là cách chắc chắn để họ cãi nhau về
 * một con số vốn giống nhau.
 *
 * Những hàm trả về CÂU CHỮ ("3 giờ trước") thì khác — chúng nhận `lang` vì file
 * này không phải component nên không gọi được `useT`.
 */

const VN = 'vi-VN';
const TZ = 'Asia/Ho_Chi_Minh';

export function formatNumber(value: number): string {
  return new Intl.NumberFormat(VN).format(Math.round(value));
}

export function formatPercent(ratio: number, digits = 0): string {
  return `${(ratio * 100).toFixed(digits)}%`;
}

export function formatDateTime(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(VN, {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: TZ,
  }).format(date);
}

export function formatDate(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(VN, { dateStyle: 'short', timeZone: TZ }).format(date);
}

/**
 * Mốc thời gian cho một DÒNG LOG — tới mili-giây, khác `formatDateTime`.
 *
 * `formatDateTime` bỏ cả giây lẫn mili-giây (`timeStyle: 'short'`), đúng cho
 * một badge "cập nhật lúc" nhưng sai ở đây: việc cần làm là biết dòng nào xảy
 * ra TRƯỚC dòng nào, và nhiều dòng log rơi vào cùng một phút, thậm chí cùng
 * một giây.
 */
const LOG_TIME_FORMAT = new Intl.DateTimeFormat(VN, {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  timeZone: TZ,
});

export function formatLogTime(ms: number): string {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return '—';
  const millis = String(((ms % 1000) + 1000) % 1000).padStart(3, '0');
  return `${LOG_TIME_FORMAT.format(date)}.${millis}`;
}

const RELATIVE_TEXT = dict({
  en: {
    upcoming: 'upcoming',
    justNow: 'just now',
    minutes: (count: number) => `${count} minutes ago`,
    hours: (count: number) => `${count} hours ago`,
    days: (count: number) => `${count} days ago`,
    months: (count: number) => `${count} months ago`,
    years: (count: number) => `${count} years ago`,
  },
  vi: {
    upcoming: 'sắp tới',
    justNow: 'vừa xong',
    minutes: (count: number) => `${count} phút trước`,
    hours: (count: number) => `${count} giờ trước`,
    days: (count: number) => `${count} ngày trước`,
    months: (count: number) => `${count} tháng trước`,
    years: (count: number) => `${count} năm trước`,
  },
});

/** "3 giờ trước", "2 ngày trước". Mốc trong tương lai hiện "sắp tới". */
export function formatRelative(
  value: string | number | null | undefined,
  lang: PanelLang,
): string {
  if (value === null || value === undefined || value === '') return '—';
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return '—';

  const t = RELATIVE_TEXT[lang];
  const deltaMs = Date.now() - time;
  if (deltaMs < 0) return t.upcoming;

  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 1) return t.justNow;
  if (minutes < 60) return t.minutes(minutes);

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t.hours(hours);

  const days = Math.floor(hours / 24);
  if (days < 30) return t.days(days);

  const months = Math.floor(days / 30);
  if (months < 12) return t.months(months);
  return t.years(Math.floor(months / 12));
}

const DURATION_TEXT = dict({
  en: {
    daysHours: (days: number, hours: number) => `${days}d ${hours}h`,
    hoursMinutes: (hours: number, minutes: number) => `${hours}h ${minutes}m`,
    minutes: (minutes: number) => `${minutes}m`,
  },
  vi: {
    daysHours: (days: number, hours: number) => `${days} ngày ${hours} giờ`,
    hoursMinutes: (hours: number, minutes: number) => `${hours} giờ ${minutes} phút`,
    minutes: (minutes: number) => `${minutes} phút`,
  },
});

/** Thời gian chạy của bot: "3 ngày 4 giờ". */
export function formatDuration(ms: number, lang: PanelLang): string {
  const totalMinutes = Math.floor(ms / 60_000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const t = DURATION_TEXT[lang];
  if (days > 0) return t.daysHours(days, hours);
  if (hours > 0) return t.hoursMinutes(hours, minutes);
  return t.minutes(minutes);
}

/** Ngày dạng `YYYY-MM-DD` → `12/07` cho trục biểu đồ. */
export function shortDay(day: string): string {
  const parts = day.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}` : day;
}

/**
 * Markdown → HTML, đủ dùng cho ô xem trước câu trả lời FAQ.
 *
 * Cố ý KHÔNG kéo một thư viện markdown đầy đủ về: câu trả lời FAQ chỉ dùng vài
 * cú pháp cơ bản, và mọi ký tự đều được escape TRƯỚC khi thay thế, nên không có
 * đường nào để nội dung FAQ chèn được thẻ HTML vào trang quản trị.
 */
export function renderMarkdown(source: string): string {
  const escaped = source
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Cả `"` nữa, không chỉ `<>&`: bước thay link ở dưới nhét URL vào trong một
    // thuộc tính `href="…"`, và một dấu `"` sống sót cho phép nội dung FAQ đóng
    // thuộc tính đó sớm rồi mở một `onmouseover=` — tức là chèn được JS vào
    // trang quản trị qua một đề xuất FAQ mà người thường viết ra.
    .replace(/"/g, '&quot;');

  const lines = escaped.split('\n');
  const out: string[] = [];
  let inList: 'ul' | 'ol' | null = null;
  let inCode = false;

  const closeList = (): void => {
    if (inList !== null) {
      out.push(`</${inList}>`);
      inList = null;
    }
  };

  for (const line of lines) {
    if (line.trimStart().startsWith('```')) {
      closeList();
      out.push(inCode ? '</code></pre>' : '<pre><code>');
      inCode = !inCode;
      continue;
    }
    if (inCode) {
      out.push(line);
      continue;
    }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading !== null) {
      closeList();
      const level = heading[1]?.length ?? 1;
      out.push(`<h${level}>${inline(heading[2] ?? '')}</h${level}>`);
      continue;
    }

    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    if (bullet !== null) {
      if (inList !== 'ul') {
        closeList();
        out.push('<ul>');
        inList = 'ul';
      }
      out.push(`<li>${inline(bullet[1] ?? '')}</li>`);
      continue;
    }

    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (numbered !== null) {
      if (inList !== 'ol') {
        closeList();
        out.push('<ol>');
        inList = 'ol';
      }
      out.push(`<li>${inline(numbered[1] ?? '')}</li>`);
      continue;
    }

    const quote = /^\s*&gt;\s?(.*)$/.exec(line);
    if (quote !== null) {
      closeList();
      out.push(`<blockquote>${inline(quote[1] ?? '')}</blockquote>`);
      continue;
    }

    if (line.trim().length === 0) {
      closeList();
      continue;
    }

    closeList();
    out.push(`<p>${inline(line)}</p>`);
  }

  closeList();
  if (inCode) out.push('</code></pre>');
  return out.join('\n');
}

function inline(text: string): string {
  return text
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
    // Chỉ chấp nhận http/https: `[x](javascript:…)` là đường chèn mã kinh điển,
    // và nội dung FAQ tới từ đề xuất của cộng đồng chứ không chỉ từ admin.
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
}

/**
 * Chuyển một giá trị `unknown` thành chuỗi hiển thị được.
 *
 * `String(value)` trên `unknown` là cái bẫy: gặp object nó cho ra
 * `[object Object]` — người dùng thấy đúng chuỗi đó trên giao diện thay vì
 * thông báo thật, và không ai biết chuyện gì đã xảy ra. Ở đây mọi dữ liệu tới
 * từ API đều là `unknown` cho tới khi được kiểm, nên chỗ này gặp thường xuyên.
 */
export function textOf(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  if (value instanceof Error) return value.message;
  try {
    return JSON.stringify(value) ?? fallback;
  } catch {
    // Cấu trúc vòng hoặc BigInt lồng bên trong — thà mất chi tiết còn hơn ném
    // lỗi ngay giữa lúc đang hiển thị một lỗi khác.
    return fallback;
  }
}
